import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
  CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import {
  Input,
  TimeInput,
  Checkbox,
  DateInput,
  DaysOfWeekSelector
} from '../../common/Input';
import { useDropdownValues } from '../../hooks/useDropdownFetch';
import {
  fetchOTTransactionTypesEnum,
  fetchDropdownTypesEnum,
  fetchFormulaTypesEnum,
  fetchDayTypesEnum,
  fetchOutputValueTypesEnum
} from "../../api/fetch";
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { transformEnumData } from "../../utils/textUtils";
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { Select } from "../../common/Select";

function OTFormulas({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Priority Level",
    "Selected Type",
    "Selected Value",
    "Transaction Type",
    "Formula Type",
    "Output Value Type",
    "Payment Value",
    "Min OT (Hrs)", // ADDED
    "Max OT (Hrs)", // ADDED
    "Duration (Days)",  // ADDED NEW COLUMN
    "Status",
    "Actions"
  ]
  var accessors = [
    "id",
    "priority",
    "dropdownTypeName",
    "dropdownValueName",
    "transactionTypeName",
    "formulaTypeName",
    "outputValueTypeName",
    "outputValue",
    "minOTHours", // ADDED
    "maxOTHours", // ADDED
    "duration",  // ADDED NEW ACCESSOR
    "isActive",
    "actions"
  ]

  const apiUrl = domain + "/api/ClientFormula";

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Enum states
  const [dropdownTypeOptions, setDropdownTypeOptions] = useState([]);
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
  const [formulaTypeOptions, setFormulaTypeOptions] = useState([]);
  const [dayTypeOptions, setDayTypeOptions] = useState([]);
  const [outputValueTypeOptions, setOutputValueTypeOptions] = useState([]);
  const [enumsLoading, setEnumsLoading] = useState(false);

  // Use the custom hook for dropdown values
  const {
    dropdownValueOptions,
    dropdownValueLoading,
    fetchDropdownValues,
    resetDropdownValues,
    clearCacheForType
  } = useDropdownValues();

  // Form state - Cleaned
  const [form, setForm] = useState({
    priority: "1",
    dropdownType: "",
    dropdownValue: "",
    transactionType: "",
    formulaType: "",
    hasAllDays: false,
    hasSunday: false,
    hasMonday: false,
    hasTuesday: false,
    hasWednesday: false,
    hasThursday: false,
    hasFriday: false,
    hasSaturday: false,
    outputValueType: "",
    outputValue: "",
    isActive: true,
    duration: "",
    minOTHours: "", // ADDED
    maxOTHours: "", // ADDED
    formulaData: {
      startTime: "",
      endTime: "",
      startMinutes: "",
      endMinutes: "",
      startDay: "",
      endDay: "",
      percentage: ""
    }
  });

  const resetLocalVariables = () => {
    setForm({
      priority: "1",
      dropdownType: "",
      dropdownValue: "",
      transactionType: "",
      formulaType: "",
      hasAllDays: false,
      hasSunday: false,
      hasMonday: false,
      hasTuesday: false,
      hasWednesday: false,
      hasThursday: false,
      hasFriday: false,
      hasSaturday: false,
      outputValueType: "",
      outputValue: "",
      isActive: true,
      duration: "",
      minOTHours: "", // ADDED
      maxOTHours: "", // ADDED
      formulaData: {
        startTime: "",
        endTime: "",
        startMinutes: "",
        endMinutes: "",
        startDay: "",
        endDay: "",
        percentage: ""
      }
    });
    setSelectedData(null);
    resetDropdownValues();
  };

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);

  const fetchEnums = useCallback(async () => {
    try {
      setEnumsLoading(true);

      const [
        transactionData,
        dropdownData,
        formulaData,
        dayData,
        outputValueData,
      ] = await Promise.all([
        fetchOTTransactionTypesEnum(),
        fetchDropdownTypesEnum(),
        fetchFormulaTypesEnum(),
        fetchDayTypesEnum(),
        fetchOutputValueTypesEnum(),
      ]);

      setTransactionTypeOptions(transformEnumData(transactionData));
      setDropdownTypeOptions(transformEnumData(dropdownData));
      
      // MODIFIED: Filter to only allow "ALL" and "TIME"
      const allFormulaOptions = transformEnumData(formulaData);
      const filteredFormulaOptions = allFormulaOptions.filter(opt => 
        opt.label.toUpperCase() === "ALL" || opt.label.toUpperCase() === "TIME"
      );
      setFormulaTypeOptions(filteredFormulaOptions);
      
      setDayTypeOptions(transformEnumData(dayData));
      setOutputValueTypeOptions(transformEnumData(outputValueData));

    } catch (error) {
      console.error("Error fetching enums:", error);
    } finally {
      setEnumsLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchEnums();
    fetchData();
  }, [fetchEnums, fetchData]);

  // Fetch dropdown values when dropdown type changes
  useEffect(() => {
    if (form.dropdownType) {
      fetchDropdownValues(form.dropdownType);
    } else {
      resetDropdownValues();
    }
  }, [form.dropdownType, fetchDropdownValues, resetDropdownValues]);


  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    var res = filterData.filter((f) =>
      f?.dropdownType?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.transactionType?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.formulaType?.toString().toLowerCase().includes(lowercaseValue) ||
      f?.priority?.toString().includes(lowercaseValue) ||
      f?.duration?.toString().includes(lowercaseValue)
    );
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prepare formulaData based on selected formulaType
    let formulaData = {};
    const formulaTypeNum = parseInt(form.formulaType);

    switch (formulaTypeNum) {
      case 1: // TIME
        formulaData = {
          startTime: form.formulaData.startTime || null,
          endTime: form.formulaData.endTime || null
        };
        break;
      case 2: // MINUTES
        formulaData = {
          startMinutes: parseInt(form.formulaData.startMinutes) || null,
          endMinutes: parseInt(form.formulaData.endMinutes) || null
        };
        break;
      case 3: // DAY
        formulaData = {
          startDay: form.formulaData.startDay || null,
          endDay: form.formulaData.endDay || null
        };
        break;
      case 4: // PERCENTAGE
        formulaData = {
          percentage: parseFloat(form.formulaData.percentage) || null
        };
        break;
      default:
        formulaData = {};
    }

    // Prepare data for API 
    const apiData = {
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      transactionType: form.transactionType ? parseInt(form.transactionType) : null,
      formulaType: form.formulaType ? parseInt(form.formulaType) : null,
      hasAllDays: form.hasAllDays || false,
      hasSunday: form.hasSunday || false,
      hasMonday: form.hasMonday || false,
      hasTuesday: form.hasTuesday || false,
      hasWednesday: form.hasWednesday || false,
      hasThursday: form.hasThursday || false,
      hasFriday: form.hasFriday || false,
      hasSaturday: form.hasSaturday || false,
      outputValueType: form.outputValueType ? parseInt(form.outputValueType) : null,
      outputValue: form.outputValue ? parseFloat(form.outputValue) : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
      duration: form.duration ? parseInt(form.duration) : null,
      minOTHours: form.minOTHours ? parseFloat(form.minOTHours) : null, // ADDED
      maxOTHours: form.maxOTHours ? parseFloat(form.maxOTHours) : null, // ADDED
      formulaData: formulaData
    };

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchData();

      showGeneralMessage("OT Formula created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create OT Formula";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    // Prepare formulaData based on selected formulaType
    let formulaData = {};
    const formulaTypeNum = parseInt(form.formulaType);

    switch (formulaTypeNum) {
      case 1: // TIME
        formulaData = {
          startTime: form.formulaData.startTime || null,
          endTime: form.formulaData.endTime || null
        };
        break;
      case 2: // MINUTES
        formulaData = {
          startMinutes: parseInt(form.formulaData.startMinutes) || null,
          endMinutes: parseInt(form.formulaData.endMinutes) || null
        };
        break;
      case 3: // DAY
        formulaData = {
          startDay: form.formulaData.startDay || null,
          endDay: form.formulaData.endDay || null
        };
        break;
      case 4: // PERCENTAGE
        formulaData = {
          percentage: parseFloat(form.formulaData.percentage) || null
        };
        break;
      default:
        formulaData = {};
    }

    // Update data for API 
    const apiData = {
      priority: parseInt(form.priority) || null,
      dropdownType: form.dropdownType ? parseInt(form.dropdownType) : null,
      dropdownValue: form.dropdownValue ? parseInt(form.dropdownValue) : null,
      transactionType: form.transactionType ? parseInt(form.transactionType) : null,
      formulaType: form.formulaType ? parseInt(form.formulaType) : null,
      hasAllDays: form.hasAllDays || false,
      hasSunday: form.hasSunday || false,
      hasMonday: form.hasMonday || false,
      hasTuesday: form.hasTuesday || false,
      hasWednesday: form.hasWednesday || false,
      hasThursday: form.hasThursday || false,
      hasFriday: form.hasFriday || false,
      hasSaturday: form.hasSaturday || false,
      outputValueType: form.outputValueType ? parseInt(form.outputValueType) : null,
      outputValue: form.outputValue ? parseFloat(form.outputValue) : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
      duration: form.duration ? parseInt(form.duration) : null,
      minOTHours: form.minOTHours ? parseFloat(form.minOTHours) : null,
      maxOTHours: form.maxOTHours ? parseFloat(form.maxOTHours) : null,
      formulaData: formulaData
    };

    console.log('Updating data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("OT Formula updated successfully!", true);
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update OT Formula";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    const formulaData = data.formulaData || {};

    setForm({
      priority: data.priority?.toString() || null,
      dropdownType: data.dropdownType?.toString() || "",
      dropdownValue: data.dropdownValue?.toString() || "",
      transactionType: data.transactionType?.toString() || "",
      formulaType: data.formulaType?.toString() || "",
      hasAllDays: data.hasAllDays || false,
      hasSunday: data.hasSunday || false,
      hasMonday: data.hasMonday || false,
      hasTuesday: data.hasTuesday || false,
      hasWednesday: data.hasWednesday || false,
      hasThursday: data.hasThursday || false,
      hasFriday: data.hasFriday || false,
      hasSaturday: data.hasSaturday || false,
      outputValueType: data.outputValueType?.toString() || "",
      outputValue: data.outputValue?.toString() || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
      duration: data.duration?.toString() || "",
      minOTHours: data.minOTHours?.toString() || "", // ADDED
      maxOTHours: data.maxOTHours?.toString() || "", // ADDED
      formulaData: {
        startTime: formulaData.startTime || "",
        endTime: formulaData.endTime || "",
        startMinutes: formulaData.startMinutes?.toString() || "",
        endMinutes: formulaData.endMinutes?.toString() || "",
        startDay: formulaData.startDay || "",
        endDay: formulaData.endDay || "",
        percentage: formulaData.percentage?.toString() || ""
      }
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Dev Mode >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"OT Formula"}</span>
              </div>
              <h3>OT Formula</h3>
            </div>
            <div className='p-4 space-y-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />

              <CreateTable
                tableName={"OTFormulas"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add OT Formula"}
        isEdit={false}
        dropdownTypeOptions={dropdownTypeOptions}
        transactionTypeOptions={transactionTypeOptions}
        formulaTypeOptions={formulaTypeOptions}
        dayTypeOptions={dayTypeOptions}
        outputValueTypeOptions={outputValueTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update OT Formula"}
        isEdit={true}
        dropdownTypeOptions={dropdownTypeOptions}
        transactionTypeOptions={transactionTypeOptions}
        formulaTypeOptions={formulaTypeOptions}
        dayTypeOptions={dayTypeOptions}
        outputValueTypeOptions={outputValueTypeOptions}
        dropdownValueOptions={dropdownValueOptions}
        dropdownValueLoading={dropdownValueLoading}
        enumsLoading={enumsLoading}
        fetchDropdownValues={fetchDropdownValues}
        resetDropdownValues={resetDropdownValues}
        clearCacheForType={clearCacheForType}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading || enumsLoading || dropdownValueLoading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  isEdit = false,
  dropdownTypeOptions,
  transactionTypeOptions,
  formulaTypeOptions,
  outputValueTypeOptions,
  dropdownValueOptions,
  dropdownValueLoading,
  enumsLoading,
  fetchDropdownValues,
  resetDropdownValues,
  clearCacheForType
}) {

  const submit = (e) => {
    e.preventDefault();

    // Debug: Check form state before submission
    console.log('Form state before submission:', {
      priority: form.priority,
      isActive: form.isActive,
      duration: form.duration,
      fullForm: form
    });

    handleSubmit(e);

    console.log({ form });
  };

  const handleClose = () => {
    onClose();
  };

  const handleInputChange = (field, value) => {
    // If dropdown type is changing, clear the cache for the old type
    if (field === 'dropdownType' && form.dropdownType !== value) {
      if (form.dropdownType) {
        clearCacheForType(form.dropdownType);
      }
      // Reset dropdown value when type changes
      setForm(prev => ({
        ...prev,
        [field]: value,
        dropdownValue: ""
      }));

      // Fetch new dropdown values
      if (value) {
        fetchDropdownValues(value);
      } else {
        resetDropdownValues();
      }
    } else {
      setForm(prev => {
        const newForm = {
          ...prev,
          [field]: value
        };

        return newForm;
      });
    }
  };

  const handleFormulaDataChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      formulaData: {
        ...prev.formulaData,
        [field]: value
      }
    }));
  };

  const handleFormulaTypeChange = (value) => {
    // Clear formula data when formula type changes
    setForm(prev => ({
      ...prev,
      formulaType: value,
      formulaData: {
        startTime: "",
        endTime: "",
        startMinutes: "",
        endMinutes: "",
        startDay: "",
        endDay: "",
        percentage: ""
      }
    }));
  };

  // Handle days of week changes
  const handleDaysChange = (daysObject) => {
    setForm(prev => ({
      ...prev,
      ...daysObject
    }));
  };

  // Render dynamic form fields based on selected formula type
  const renderFormulaFields = () => {
    const formulaTypeNum = parseInt(form.formulaType);

    switch (formulaTypeNum) {
      case 1: // TIME
        return (
          <div className="grid grid-cols-2 gap-3">
            <TimeInput
              label="Start Time"
              value={form.formulaData.startTime || ""}
              onChange={(e) => handleFormulaDataChange('startTime', e.target.value)}
              isRequired={true}
            />
            <TimeInput
              label="End Time"
              value={form.formulaData.endTime || ""}
              onChange={(e) => handleFormulaDataChange('endTime', e.target.value)}
              isRequired={true}
            />
          </div>
        );

      case 2: // MINUTES
        return (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Start Minutes"
              value={form.formulaData.startMinutes || ""}
              onChange={(e) => handleFormulaDataChange('startMinutes', e.target.value)}
              placeholder="0"
              type="number"
              min="0"
              isRequired={true}
            />
            <Input
              label="End Minutes"
              value={form.formulaData.endMinutes || ""}
              onChange={(e) => handleFormulaDataChange('endMinutes', e.target.value)}
              placeholder="0"
              type="number"
              min="0"
              isRequired={true}
            />
          </div>
        );

      case 3: // DAY
        return (
          <div className="grid grid-cols-2 gap-3">
            <DateInput
              label="Start Day"
              value={form.formulaData.startDay || ""}
              onChange={(e) => handleFormulaDataChange('startDay', e.target.value)}
              isRequired={true}
            />
            <DateInput
              label="End Day"
              value={form.formulaData.endDay || ""}
              onChange={(e) => handleFormulaDataChange('endDay', e.target.value)}
              isRequired={true}
            />
          </div>
        );

      case 4: // PERCENTAGE
        return (
          <Input
            label="Percentage"
            value={form.formulaData.percentage || ""}
            onChange={(e) => handleFormulaDataChange('percentage', e.target.value)}
            placeholder="0"
            type="number"
            step="0.01"
            min="0"
            max="100"
            isRequired={true}
          />
        );

      default:
        return null;
    }
  };

  // This would fetch actual options from API based on dropdown type
  const getDropdownLabel = () => {
    const dropdownOption = dropdownTypeOptions.find(opt => opt.value.toString() === form.dropdownType);
    return dropdownOption ? `${dropdownOption.label}` : 'Value';
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[60%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">

            {/* —————————————————————————————————————————————————————————————————————————————————————— */}
            {/* ADDED SECTION: Operational Flow & Logic Guide */}
            {/* —————————————————————————————————————————————————————————————————————————————————————— */}
            <div className="border border-blue-200 rounded-lg bg-blue-50 px-4 py-3 shadow-sm">
              <div className="flex items-center space-x-2 mb-2 border-b border-blue-200 pb-1">
                <InfoIcon className="text-blue-500" fontSize="small" />
                <h3 className="font-semibold text-blue-800 uppercase tracking-wider text-sm">Operational Flow & Logic Guide</h3>
              </div>
              <div className="text-sm text-blue-900 space-y-2 leading-relaxed">
                <p>
                  This module defines the <strong>Overtime Restriction and Rate rules</strong> applied during payroll processing.
                  When an employee files for OT, the system executes the following flow:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Target Selection (Who):</strong> Uses <em>Dynamic Filtering</em> to identify specific employees based on organizational metadata (Location, Dept, etc.).</li>
                  <li><strong>Trigger (What):</strong> Matches the rule to a specific <em>Transaction Type</em> (e.g., Regular vs. Holiday OT).</li>
                  <li><strong>Constraint (When):</strong> Applies <em>Formula Types</em> to validate if the OT occurred within specific timeframes or date ranges.</li>
                  <li><strong>Filing Window (Duration):</strong> Enforces a maximum "Days to File" limit based on the event date.</li>
                  <li><strong>Result (Output):</strong> Calculates the final rate using either a fixed amount or a multiplier (Percentage).</li>
                </ul>
                <p className="text-xs italic bg-blue-100 p-2 rounded">
                  <strong>Developer Note:</strong> If multiple rules match an employee, the system resolves conflicts by selecting the entry with the <strong>lowest Priority Level</strong> (1 being highest).
                </p>
              </div>
            </div>
            {/* —————————————————————————————————————————————————————————————————————————————————————— */}

            {/* Priority Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Priority Rules</h3>
                <div className="text-sm text-gray-500 italic">
                  * Lower priority numbers are selected first. 1 = highest priority
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
                <Input
                  label="Priority Level"
                  value={form.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  placeholder="Enter priority number (e.g. 1, 2)"
                  type="number"
                  min="1"
                  max="999"
                  isRequired={true}
                />
              </div>
            </div>

            {/* Dynamic Filtering */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Dynamic Filtering (Who)</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3 space-y-3">
                <Select
                  label="Selected Type"
                  value={form.dropdownType}
                  onChange={(e) => handleInputChange('dropdownType', e.target.value)}
                  options={dropdownTypeOptions}
                  placeholder={enumsLoading ? "Loading types..." : "Select type"}
                  isRequired={true}
                  isDisabled={enumsLoading}
                />

                {form.dropdownType && (
                  <Select
                    label={getDropdownLabel()}
                    value={form.dropdownValue}
                    onChange={(e) => handleInputChange('dropdownValue', e.target.value)}
                    options={dropdownValueOptions}
                    placeholder={dropdownValueLoading ? "Loading options..." : `Select ${getDropdownLabel()}`}
                    isRequired={true}
                    isDisabled={dropdownValueLoading}
                    className={"!mt-0"}
                    parentClassName={"!mt-0"}
                  />
                )}
              </div>
            </div>

            {/* Transaction Type */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Transaction Type (What)</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
                <Select
                  label="Transaction Type"
                  value={form.transactionType}
                  onChange={(e) => handleInputChange('transactionType', e.target.value)}
                  options={transactionTypeOptions}
                  placeholder={enumsLoading ? "Loading transaction types..." : "Select transaction type"}
                  isRequired={true}
                  isDisabled={enumsLoading}
                />
              </div>
            </div>

            {/** When */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Date | Time Applicable (When)</h3>
              </div>
              {/* Date Selections Checkbox */}
              <div className="pb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Days
                </label>
                <DaysOfWeekSelector
                  value={{
                    hasAllDays: form.hasAllDays || false,
                    hasSunday: form.hasSunday || false,
                    hasMonday: form.hasMonday || false,
                    hasTuesday: form.hasTuesday || false,
                    hasWednesday: form.hasWednesday || false,
                    hasThursday: form.hasThursday || false,
                    hasFriday: form.hasFriday || false,
                    hasSaturday: form.hasSaturday || false,
                  }}
                  onChange={handleDaysChange}
                  disabled={false}
                />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
                {/* Formula Classification */}
                <div className="space-y-4">
                  <Select
                    label="Formula Type"
                    value={form.formulaType}
                    onChange={(e) => handleFormulaTypeChange(e.target.value)}
                    options={formulaTypeOptions}
                    placeholder={enumsLoading ? "Loading formula types..." : "Select formula type"}
                    isRequired={true}
                    isDisabled={enumsLoading}
                  />

                  {form.formulaType && (
                    <div className="">
                      <div className="mb-2 text-sm font-medium text-gray-700">Formula Parameters:</div>
                      {renderFormulaFields()}
                    </div>
                  )}
                </div>

                {/* Duration Section */}
                <div className="space-y-4">
                  <Input
                    label="Duration to File (Days)"
                    value={form.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="Enter duration in days (e.g., 1, 2, 3)"
                    type="number"
                    min="1"
                    max="999"
                    isRequired={true}
                    tooltip="Number of days allowed to file for this OT"
                  />
                </div>
              </div>
            </div>

            {/* ADDED SECTION: Filing Constraints */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Filing Constraints (Optional)</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
                <Input
                  label="Minimum OT Hours"
                  value={form.minOTHours}
                  onChange={(e) => handleInputChange('minOTHours', e.target.value)}
                  placeholder="e.g. 0.5"
                  type="number"
                  step="0.01"
                />
                <Input
                  label="Maximum OT Hours"
                  value={form.maxOTHours}
                  onChange={(e) => handleInputChange('maxOTHours', e.target.value)}
                  placeholder="e.g. 8"
                  type="number"
                  step="0.01"
                />
              </div>
            </div>

            {/* Output Rules */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Payment Type</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 space-y-3">
                <Select
                  label="Payment Value Type"
                  value={form.outputValueType}
                  onChange={(e) => handleInputChange('outputValueType', e.target.value)}
                  options={outputValueTypeOptions}
                  placeholder={enumsLoading ? "Loading output types..." : "Select Payment value type"}
                  isRequired={true}
                  isDisabled={enumsLoading}
                />

                <Input
                  label="Payment Value"
                  value={form.outputValue}
                  onChange={(e) => handleInputChange('outputValue', e.target.value)}
                  placeholder={form.outputValueType === "1" ? "Enter percentage (e.g., 20%, 30%, 45%)" : "Enter amount"}
                  type="number"
                  step="0.01"
                  min="0"
                  isRequired={true}
                  className={"!mt-0"}
                  parentClassName={"!mt-0"}
                />
              </div>
            </div>

            {/* Status Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Status</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Checkbox
                  label="Active"
                  checked={form.isActive !== undefined ? form.isActive : true}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  tooltip="Enable or disable this formula"
                />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white shadow-sm">
            <button
              onClick={handleClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
              disabled={enumsLoading || dropdownValueLoading}
            >
              {isEdit ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OTFormulas;